import { EffectCarousel, Drawer, CustomButton, Popover } from "theme";
import { inView } from "vendor";

const openedHotSpots = new Set();

class HotSpotPopover extends Popover {
  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute("open-on-view")) {
      return;
    }

    const section = this.closest(".shopify-section");
    if (!section) {
      return;
    }

    inView(section, () => {
      if (this.open || openedHotSpots.has(this.id) || !matchMedia("screen and (min-width: 1000px)").matches) {
        return;
      }

      openedHotSpots.add(this.id);
      this._openedAutomatically = true;
      this.show();
    });
  }

  get shouldTrapFocus() {
    return !this._openedAutomatically;
  }

  _allowOutsideClick(event) {
    if (!this._openedAutomatically) {
      return super._allowOutsideClick(event);
    }

    const opener = event.target.closest?.(`[aria-controls="${this.id}"]`);
    if (opener) {
      this._openedAutomatically = false;
      return super._allowOutsideClick(event);
    }

    return false;
  }

  _onToggleClicked(event) {
    this._openedAutomatically = false;
    super._onToggleClicked(event);
  }
}

if (!window.customElements.get("hot-spot-popover")) {
  window.customElements.define("hot-spot-popover", HotSpotPopover);
}

// Enhance Accessability and skip header navigation with keyboard
document.querySelector('.skip-to-content').addEventListener('click', function(e) {
  const main = document.getElementById('main');
  main.setAttribute('tabindex', '-1');
  main.focus();
});

var OrtliebAnnouncementBar = class extends EffectCarousel {
  connectedCallback() {
    super.connectedCallback();
    this.updateCarouselForScreenSize();
    window.addEventListener(
      "resize",
      this.updateCarouselForScreenSize.bind(this)
    );
  }

  // Override the swipeable getter to enable swipe only on mobile
  get swipeable() {
    return window.innerWidth < 700;
  }

  updateCarouselForScreenSize() {
    const isMobile = window.innerWidth < 700;

    if (isMobile) {
      // Adjust the carousel for mobile
      this.enableCarousel();
    } else {
      // Adjust the carousel for desktop
      this.disableCarousel();
    }
  }

  enableCarousel() {
    // Show only the selected item
    this.items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("is-selected");
        item.classList.remove("reveal-invisible");
      } else {
        item.classList.remove("is-selected");
        item.classList.add("reveal-invisible");
      }
    });
  }

  disableCarousel() {
    // Show all items
    this.items.forEach((item) => {
      item.classList.add("is-selected");
      item.classList.remove("reveal-invisible");
    });
  }

  // Override the _transitionTo method to control transitions
  _transitionTo(fromSlide, toSlide, options = {}) {
    if (this.swipeable) {
      // Only perform transitions when swipeable (on mobile)
      fromSlide.classList.remove("is-selected");
      fromSlide.classList.add("reveal-invisible");
      toSlide.classList.add("is-selected");
      toSlide.classList.remove("reveal-invisible");
    } else {
      // On desktop, no transition needed as all items are visible / CSS handles the positioning
    }
  }
};
// Register the custom element if not already registered
if (!window.customElements.get("ortlieb-announcement-bar")) {
  window.customElements.define(
    "ortlieb-announcement-bar",
    OrtliebAnnouncementBar
  );
}

// ORTLIEB: Gallery More Button Component for limiting the gallery Height to 1000px
class GalleryMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.isExpanded = false;
    this.storageKey = null;
    this._abortController = null;
  }

  connectedCallback() {
    // Initialize AbortController for clean event listener management
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    // Nur auf Bildschirmen ab 1000px Breite aktivieren
    if (window.innerWidth < 1000) {
      this.setAttribute("hidden", "");
      return;
    }

    this.cacheElements();
    this.initializeStorageKey();
    this.restoreExpandedState();
    
    // Click-Handler wird automatisch über onclick aufgerufen
    this.onclick = this.handleExpandClick.bind(this);
    
    // Window resize observer mit AbortController
    window.addEventListener("resize", this.handleResize.bind(this), { signal });
    
    this.checkHeight();
    this.observeResize();
  }

  disconnectedCallback() {
    // Abort all event listeners at once
    this._abortController?.abort();
    this._abortController = null;
    this.cleanup();
  }

  cacheElements() {
    // Find the gallery wrapper in the parent container
    const parentContainer =
      this.closest(".product-gallery") || this.parentElement;
    this.galleryWrapper = parentContainer.querySelector(
      ".product-gallery__ar-wrapper"
    );
    this.mediaListWrapper = parentContainer.querySelector(
      ".product-gallery__media-list-wrapper"
    );
  }

  initializeStorageKey() {
    // Simple key since we're using in-memory storage that resets on page refresh
    this.storageKey = "gallery-expanded";

    // Initialize in-memory storage if not exists
    window.galleryExpandedState = window.galleryExpandedState || {};
  }

  restoreExpandedState() {
    if (!this.storageKey) return;

    // Check in-memory storage only
    const wasExpanded =
      window.galleryExpandedState &&
      window.galleryExpandedState[this.storageKey] === true;

    if (wasExpanded && this.galleryWrapper && this.mediaListWrapper) {
      // Apply expanded state immediately
      this.isExpanded = true;
      this.setAttribute("hidden", "");
      this.setAttribute("aria-expanded", "true");
      this.galleryWrapper.classList.add("expanded");
      this.galleryWrapper.style.maxHeight = "none";
    }
  }

  handleResize() {
    if (window.innerWidth < 1000) {
      this.setAttribute("hidden", "");
      this.cleanup();
      return;
    }

    this.removeAttribute("hidden");

    // Re-initialize if elements are available
    if (!this.galleryWrapper || !this.mediaListWrapper) {
      this.cacheElements();
    }

    this.checkHeight();
    this.observeResize();
  }

  observeResize() {
    if (!this.mediaListWrapper || this.resizeObserver) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.checkHeight();
    });
    this.resizeObserver.observe(this.mediaListWrapper);
  }

  checkHeight() {
    if (!this.galleryWrapper || !this.mediaListWrapper) return;

    // If already expanded, don't show the button and keep expanded state
    if (this.isExpanded) {
      this.setAttribute("hidden", "");
      this.galleryWrapper.classList.add("expanded");
      return;
    }

    // Fix if one variant has way more images than the other.
    if (this.mediaListWrapper.scrollHeight > 1100) {
      this.removeAttribute("hidden");
      // Add a class to indicate the expand button is visible.
      this.galleryWrapper.classList.remove("expanded");
    } else {
      this.setAttribute("hidden", "");
      this.galleryWrapper.classList.add("expanded");
    }
  }

  handleExpandClick(event) {
    event.preventDefault();

    if (!this.galleryWrapper || !this.mediaListWrapper || this.isExpanded)
      return;

    this.isExpanded = true;
    this.setAttribute("hidden", "");
    this.setAttribute("aria-expanded", "true");

    // Store expanded state in memory only (resets on page refresh)
    if (this.storageKey) {
      window.galleryExpandedState = window.galleryExpandedState || {};
      window.galleryExpandedState[this.storageKey] = true;
    }

    // Calculate the new max-height and the duration of the animation.
    const fullHeight = this.mediaListWrapper.scrollHeight;
    const currentHeight = this.galleryWrapper.clientHeight;
    const extraHeight = fullHeight - currentHeight;

    const durationSec = extraHeight / 1500; // Speed = 1500px per second
    this.galleryWrapper.style.transition = `max-height ${durationSec}s ease-in-out`;

    // Set the max-height to the full height.
    this.galleryWrapper.style.maxHeight = fullHeight + "px";

    // Cleanup after the max-height transition ends.
    this.galleryWrapper.addEventListener(
      "transitionend",
      this.handleTransitionEnd.bind(this)
    );
  }

  handleTransitionEnd(event) {
    if (event.propertyName !== "max-height") return;

    this.galleryWrapper.classList.add("expanded");
    this.galleryWrapper.style.maxHeight = "none";
    this.cleanup();
    this.galleryWrapper.removeEventListener(
      "transitionend",
      this.handleTransitionEnd
    );
  }

  cleanup() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

// Register the custom element as customized built-in button
if (!window.customElements.get("gallery-more-button")) {
  window.customElements.define("gallery-more-button", GalleryMoreButton, {
    extends: "button",
  });
}

class AccountSheetTrigger extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();

    this.querySelector("button")?.addEventListener(
      "click",
      () => {
        const account = this.closest(".panel-footer__account")?.querySelector(
          "shopify-account"
        );
        const accountButton = account?.shadowRoot?.querySelector(
          "button, [role=\"button\"]"
        );

        accountButton?.click();
      },
      { signal: this._abortController.signal }
    );
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;
  }
}

if (!window.customElements.get("account-sheet-trigger")) {
  window.customElements.define("account-sheet-trigger", AccountSheetTrigger);
}

class CollapsibleText extends HTMLElement {
  connectedCallback() {
    this._abortController?.abort();
    this._abortController = new AbortController();

    const { signal } = this._abortController;

    this._button = this.querySelector("[data-collapsible-trigger]");
    this._preview = this.querySelector("[data-collapsible-preview]");
    this._content = this.querySelector("[data-collapsible-content]");

    if (!this._preview || !this._content) {
      return;
    }

    this._button?.addEventListener(
      "click",
      () => this.expand(),
      { signal }
    );

    this._content.addEventListener(
      "beforematch",
      () => this.expand(),
      { signal }
    );
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;
  }

  expand() {
    if (!this._preview || !this._content) {
      return;
    }

    if (!this.hasAttribute("data-collapsed")) {
      return;
    }

    this.removeAttribute("data-collapsed");
    this.setAttribute("data-expanded", "");

    this._button?.setAttribute("aria-expanded", "true");

    // Synchron ausführen, damit das Layout angepasst ist,
    // bevor der Browser zum Suchtreffer scrollt.
    this._content.removeAttribute("hidden");
    this._preview.hidden = true;
  }
}

if (!window.customElements.get("collapsible-text")) {
  window.customElements.define("collapsible-text", CollapsibleText);
}

// === GeoIP Popup Manager ===
class GeoIPPopupManager {
  constructor() {
    this.elements = {};
    this.originalLangOptions = [];
    this.isInitialized = false;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.cacheElements();
    this.setupEventListeners();
    
    const browserLang = (navigator.language || navigator.userLanguage || "").split("-")[0].toLowerCase();
    this.translateTexts(browserLang);

    this.checkAndShowPopup();

    this.isInitialized = true;
  }

cacheElements() {
    this.elements = {
      popup: document.getElementById("geoip-popup"),
      form: document.getElementById("geoip-popup-form"),
      trigger: document.getElementById("geoip-popup-trigger"),
      countrySelect: document.querySelector('select[name="geoip-country-select"]'),
      languageSelect: document.querySelector('select[name="geoip-language-select"]'),
    };

    if (this.elements.languageSelect) {
      this.originalLangOptions = Array.from(this.elements.languageSelect.options);
    }
  }

  setupEventListeners() {
    this.elements.form?.addEventListener("submit", this.handleFormSubmit.bind(this));
    this.elements.trigger?.addEventListener("click", this.handleManualTrigger.bind(this));
    this.elements.countrySelect?.addEventListener("change", this.populateLanguages.bind(this));

    this.populateLanguages();
  }

  translateTexts(lang) {
    let activeLangClass = `geoip-lang-${lang}`;
    const hasTranslations = document.querySelector(`.${activeLangClass}`) !== null;

    // Fallback to English ONLY for UI texts if language is not supported
    if (!hasTranslations) {
      activeLangClass = "geoip-lang-en";
    }

    document.querySelectorAll(".geoip-multilang").forEach(el => el.style.display = "none");
    document.querySelectorAll(`.${activeLangClass}`).forEach(el => el.style.display = "block");
  }

  shouldShowPopup() {
    return (
      window.geoip_enabled === true &&
      localStorage.getItem("theme:geoip-popup-shown") !== "true" &&
      sessionStorage.getItem("theme:geoip-popup-shown") !== "true"
    );
  }

  cacheCountryData(geoipResponse) {
    try {
      const dataToCache = {
        country: {
          iso_code: geoipResponse.country.iso_code,
        },
        cached_at: Date.now(),
      };

      this.setCookie(
        "geoip_country_cache",
        JSON.stringify(dataToCache),
        30,
        ".ortlieb.com"
      );
    } catch (error) {
      console.warn("Failed to cache GeoIP data:", error);
    }
  }

  getCachedCountry() {
    const cachedData = this.getCookie("geoip_country_cache");

    if (!cachedData) return null;

    try {
      const data = JSON.parse(cachedData);
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      if (data.cached_at && data.cached_at < thirtyDaysAgo) {
        this.deleteCookie("geoip_country_cache", ".ortlieb.com");
        return null;
      }

      if (data && data.country && data.country.iso_code) {
        return data;
      }
    } catch (error) {
      console.warn("Invalid cached GeoIP data:", error);
      this.deleteCookie("geoip_country_cache", ".ortlieb.com");
    }

    return null;
  }

  setCookie(name, value, days, domain) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    const domainPart = domain ? `;domain=${domain}` : "";
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax${domainPart}`;
  }

  getCookie(name) {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length);
    }
    return null;
  }

  deleteCookie(name, domain) {
    const domainPart = domain ? `;domain=${domain}` : "";
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/${domainPart}`;
  }

  async checkAndShowPopup() {
    if (!this.shouldShowPopup()) return;

    const cachedCountry = this.getCachedCountry();

    if (cachedCountry) {
      this.processGeoIPResponse(cachedCountry);
      return;
    }

    this.loadShopifyPrivacyAPI();
  }

  loadShopifyPrivacyAPI() {
    if (typeof window.Shopify === "undefined") {
      console.warn("Shopify object not available");
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      (error) => {
        if (error) {
          console.warn("Failed to load Shopify privacy API:", error);
          return;
        }

        this.handleShopifyPrivacyLoad();
      }
    );
  }

  handleShopifyPrivacyLoad() {
    try {
      const region = window.Shopify.customerPrivacy.getRegion();
      if (!region || region.length < 2) return;

      const response = { country: { iso_code: region.slice(0, 2) } };

      this.cacheCountryData(response);
      this.processGeoIPResponse(response);
    } catch (error) {
      console.warn("Shopify privacy API detection failed:", error);
    }
  }

  processGeoIPResponse(response) {
    try {
      const userCountry = response.country.iso_code.toLowerCase();
      const storeCountry = (window.storeCountry || "").toLowerCase();

      // Finde die exakte Option im DOM, um Case-Mismatch (z.B. "de" vs "DE") zu vermeiden
      const targetOption = Array.from(this.elements.countrySelect.options).find(
        (option) => option.value.toLowerCase() === userCountry
      );

      if (targetOption && userCountry !== storeCountry) {
        this.showPopup();
        this.preselectCountry(targetOption.value);
        sessionStorage.setItem("theme:geoip-popup-shown", "true");
      } else if (userCountry !== storeCountry) {
        this.checkBrowserLanguageForCurrentStore();
      }
    } catch (error) {
      console.error("Error processing GeoIP response:", error);
    }
  }

  checkBrowserLanguageForCurrentStore() {
    if (!this.elements.countrySelect || !this.elements.languageSelect) return;

    try {
      const browserLang = (navigator.language || navigator.userLanguage || "").split("-")[0].toLowerCase();
      const currentLang = (window.currentLanguage || "").toLowerCase();

      this.translateTexts(browserLang);

      if (browserLang === currentLang) return;

      const currentStoreOption = Array.from(this.elements.countrySelect.options).find(
        (option) => option.value.toLowerCase() === window.storeCountry?.toLowerCase()
      );

      if (!currentStoreOption) return;

      const allowedLanguages = currentStoreOption.getAttribute("data-languages")?.toLowerCase().split(",") || [];
      const hasMatchingLang = allowedLanguages.includes(browserLang);
      const hasEnglishLang = allowedLanguages.includes("en");

      if (hasMatchingLang) {
        this.showPopup();
        this.elements.countrySelect.value = currentStoreOption.value;
        this.populateLanguages();
        this.elements.languageSelect.value = browserLang;
        sessionStorage.setItem("theme:geoip-popup-shown", "true");
      } 
      else if (hasEnglishLang && currentLang !== "en") {
        this.showPopup();
        this.elements.countrySelect.value = currentStoreOption.value;
        this.populateLanguages();
        this.elements.languageSelect.value = "en";
        sessionStorage.setItem("theme:geoip-popup-shown", "true");
      }
    } catch (error) {
      console.warn("Error checking browser language for current store:", error);
    }
  }

  showPopup() {
    this.elements.popup?.show();
  }

  preselectCountry(countryCode) {
    if (this.elements.countrySelect) {
      this.elements.countrySelect.value = countryCode;
      this.elements.countrySelect.dispatchEvent(new Event("change"));
    }
  }

  handleManualTrigger(event) {
    event.stopPropagation();

    if (window.storeCountry && this.elements.countrySelect) {
      this.elements.countrySelect.value = window.storeCountry.toUpperCase();
      this.elements.countrySelect.dispatchEvent(new Event("change"));
    }

    this.showPopup();
  }

  populateLanguages() {
    if (!this.elements.countrySelect || !this.elements.languageSelect || !this.originalLangOptions) return;

    try {
      const selectedOption = this.elements.countrySelect.options[this.elements.countrySelect.selectedIndex];
      if (!selectedOption) return;
      
      const allowedLanguages = selectedOption.getAttribute("data-languages")?.toLowerCase().split(",") || [];
      const defaultLanguage = selectedOption.getAttribute("data-default-language")?.toLowerCase();
      const browserLang = (navigator.language || navigator.userLanguage || "").split("-")[0].toLowerCase();

      // Verstecke die gesamte Select-UI, wenn nur 1 Sprache für das Land existiert
      const languageControl = this.elements.languageSelect.closest(".form-control");
      if (languageControl) {
        languageControl.style.display = allowedLanguages.length <= 1 ? "none" : "";
      }

      // Speichere die aktuelle Auswahl VOR dem Leeren des DOMs
      const currentSelectedLang = this.elements.languageSelect.value.toLowerCase();

      // DOM-Element leeren (die schnellste Methode dafür)
      this.elements.languageSelect.replaceChildren();

      let firstVisibleOption = null;

      // Hänge nur die erlaubten Optionen aus dem Cache wieder in das Select ein
      this.originalLangOptions.forEach((option) => {
        if (allowedLanguages.includes(option.value.toLowerCase())) {
          this.elements.languageSelect.appendChild(option);
          if (!firstVisibleOption) firstVisibleOption = option;
        }
      });

      // Priority-based Preselect Logik
      if (allowedLanguages.includes(browserLang)) {
        this.elements.languageSelect.value = browserLang;
      }
      else if (allowedLanguages.includes("en")) {
        this.elements.languageSelect.value = "en";
      }
      else if (!allowedLanguages.includes(currentSelectedLang)) {
        if (allowedLanguages.includes(defaultLanguage)) {
          this.elements.languageSelect.value = defaultLanguage;
        } else if (firstVisibleOption) {
          this.elements.languageSelect.value = firstVisibleOption.value;
        }
      }
      
      // Update erzwingen
      this.elements.languageSelect.dispatchEvent(new Event("change"));
    } catch (error) {
      console.error("Error populating languages:", error);
    }
  }

  buildRedirectURL(selectedOption, selectedLanguage, isStrictRedirect) {
    let url = selectedOption.getAttribute("data-url");
    const defaultLang = selectedOption.getAttribute("data-default-language");

    if (selectedLanguage && selectedLanguage !== defaultLang) {
      url = url.replace(/\/$/, "") + "/" + selectedLanguage + "/";
    }

    if (!isStrictRedirect) {
      url = url.replace(/\/$/, "");
      let path = window.location.pathname;
      path = path.replace(/^\/[a-zA-Z]{2}(-[a-zA-Z]{2})?(\/|$)/, "/");

      if (!path.startsWith("/")) path = "/" + path;
      url += path;
    }

    if (window.location.search) {
      url += window.location.search;
    }

    return url;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    try {
      const selectedOption = this.elements.countrySelect.options[this.elements.countrySelect.selectedIndex];
      if (!selectedOption) return;

      const selectedLanguage = this.elements.languageSelect.value;
      const isStrictRedirect = selectedOption.getAttribute("data-strict_redirect") === "true";

      const redirectURL = this.buildRedirectURL(
        selectedOption,
        selectedLanguage,
        isStrictRedirect
      );

      if (redirectURL) {
        localStorage.setItem("theme:geoip-popup-shown", "true");
        window.location.href = redirectURL;
      }
    } catch (error) {
      console.error("Error handling form submission:", error);
    }
  }
}

// === Custom Element Registration ===
class GeoipPopup extends Drawer {
  connectedCallback() {
    super.connectedCallback();
  }

  get initialFocus() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  _setInitialPosition() {
    this.style.top = null;
    this.style.bottom = "0px";
    this.style.left = document.dir === "ltr" ? null : "0px";
    this.style.right = document.dir === "rtl" ? "auto" : "0px";
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
  }
}

// Register custom element
if (!window.customElements.get("geoip-popup")) {
  window.customElements.define("geoip-popup", GeoipPopup);
}


// === Initialize on DOM Ready ===
document.addEventListener("DOMContentLoaded", function () {
  // Initialize GeoIP popup manager
  window.geoipManager = new GeoIPPopupManager();
});

// x-modal: erweitert Drawer, überschreibt nur Position & Animation für zentrierte Darstellung

var Modal = class extends Drawer {
  get initialFocus() {
    return false;
  }
  get shouldAppendToBody() {
    return false;
  }
  _setInitialPosition() {
    // Remove any inline positioning styles set by Drawer
    this.style.left = "";
    this.style.right = "";
    this.style.top = "";
    this.style.bottom = "";
  }
};

if (!window.customElements.get("x-modal")) {
  window.customElements.define("x-modal", Modal);
}

// === OnSite Search Component ===
class OnSiteSearchBar extends HTMLElement {
  constructor() {
    super();
    this.noResultsMessage = null;
  }

  connectedCallback() {
    this.cacheElements();
    this.setupEventListeners();
  }

  cacheElements() {
    this.form = document.getElementById("onSiteSearchForm");
    this.input = document.getElementById("onSiteSearch");
    this.noResultsMessage = document.querySelector(
      ".onsitesearch-error-message"
    );
    this.searchArea = document.querySelector(".search__area");
  }

  setupEventListeners() {
    // Echtzeit-Filterung bei jeder Eingabe
    this.input?.addEventListener("input", () => this.onSiteFilter());

    // Event-Handler für Submit
    this.form?.addEventListener("submit", (e) => {
      e.preventDefault();
      this.performSearch();
    });

    // Event-Handler für Reset
    this.form?.addEventListener("reset", () => {
      this.removeFilteredSection();
      this.noResultsMessage?.classList.add("hidden");
      setTimeout(() => this.onSiteFilter(), 0);
    });
  }

  performSearch() {
    const filtered = document.getElementById("filteredSection");
    if (filtered) {
      filtered.scrollIntoView({ behavior: "smooth" });
      // Nach dem Scroll den Fokus auf search__area setzen
      setTimeout(() => this.setFocusOnSearchArea(), 100);
    } else {
      // Auch bei keinen Ergebnissen den Fokus setzen
      this.setFocusOnSearchArea();
    }
  }

  setFocusOnSearchArea() {
    const filteredSection = document.getElementById("filteredSection");
    if (!filteredSection) {
      this.fallbackFocus();
      return;
    }

    // Fokussierbare Elemente suchen (summary hat Priorität)
    const focusableElements = filteredSection.querySelectorAll(
      'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
      return;
    }

    this.fallbackFocus();
  }

  fallbackFocus() {
    // Fallback: Fokus auf search__area setzen
    if (this.searchArea) {
      // Div fokussierbar machen falls noch nicht geschehen
      if (!this.searchArea.hasAttribute("tabindex")) {
        this.searchArea.setAttribute("tabindex", "-1");
      }
      this.searchArea.focus();
    }
  }

  removeFilteredSection() {
    const prev = document.getElementById("filteredSection");
    if (prev) prev.remove();
  }

  onSiteFilter() {
    const filter = this.input.value.trim();
    const normalizedFilter = this.normalizeText(filter.toUpperCase());

    // Gefilterte Section entfernen
    this.removeFilteredSection();

    // Alle zuvor versteckten Sections wieder einblenden
    document
      .querySelectorAll("section[data-onsite-search-hidden]")
      .forEach((section) => {
        section.style.removeProperty("display");
        section.removeAttribute("data-onsite-search-hidden");
      });

    if (filter === "") {
      // Suche geleert: keine weiteren Aktionen nötig
      this.noResultsMessage?.classList.add("hidden");
      return;
    }

    // Alle Items selektieren und filtern
    const items = document.querySelectorAll(".onsite-search__item");
    const matches = [];
    // Set für alle relevanten Section-Klassen
    const sectionClassSet = new Set();
    // Set für alle Sections mit search items (werden immer ausgeblendet beim Filtern)
    const allSectionsWithItems = new Set();

    items.forEach((item) => {
      // Zugehörige Section suchen und deren Klassen merken
      const section = item.closest("section");
      if (section) {
        section.classList.forEach((cls) => sectionClassSet.add(cls));
        // Alle Sections mit Items sammeln (für Ausblendung)
        allSectionsWithItems.add(section);
      }

      const text = this.normalizeText(item.textContent);
      // data-tags durchsuchen - sowohl am item selbst als auch in Kindelementen
      let allTags = item.getAttribute("data-tags") || "";
      // Zusätzlich alle data-tags von Kindelementen sammeln
      const childElements = item.querySelectorAll("[data-tags]");
      childElements.forEach((child) => {
        const childTags = child.getAttribute("data-tags") || "";
        if (childTags) {
          allTags += " " + childTags;
        }
      });
      const tagsNormalized = this.normalizeText(allTags);
      if (
        text.includes(normalizedFilter) ||
        tagsNormalized.includes(normalizedFilter)
      ) {
        const clone = item.cloneNode(true);
        // alle <details> innerhalb der Kopie zuklappen
        clone
          .querySelectorAll("details[open]")
          .forEach((d) => d.removeAttribute("open"));
        // .accordion__content auf opacity: 0 setzen
        clone
          .querySelectorAll(".accordion__content")
          .forEach((ac) => (ac.style.opacity = "0"));
        matches.push(clone);
      }
    });

    // Alle Sections mit search items ausblenden (unabhängig von Treffern)
    allSectionsWithItems.forEach((section) => {
      section.style.display = "none";
      section.setAttribute("data-onsite-search-hidden", "1");
    });

    // Neue Section nur bei Treffern erzeugen
    if (matches.length) {
      const section = document.createElement("section");
      // Alle gesammelten Klassen übernehmen
      section.className =
        Array.from(sectionClassSet).join(" ") || "shopify-section";
      section.id = "filteredSection";
      // inline style für background-hash setzen
      section.style.setProperty("--section-background-hash", "0");

      const wrap = document.createElement("div");
      wrap.className = "section section-blends section-full";

      const faqDiv = document.createElement("div");
      faqDiv.className = "onsite-search__grid";
      matches.forEach((mi) => faqDiv.appendChild(mi));

      wrap.appendChild(faqDiv);
      section.appendChild(wrap);

      // Nach dem <section>, das die Suche enthält, einfügen
      const searchSection = this.closest("section");
      if (searchSection && searchSection.parentNode) {
        if (searchSection.nextSibling) {
          searchSection.parentNode.insertBefore(
            section,
            searchSection.nextSibling
          );
        } else {
          searchSection.parentNode.appendChild(section);
        }
      }

      this.noResultsMessage?.classList.add("hidden");
    } else {
      this.noResultsMessage?.classList.remove("hidden");
    }
  }

  normalizeText(text) {
    return text
      .replace(/[ _-]/g, "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
  }
}

// Register custom element
if (!window.customElements.get("onsite-search-bar")) {
  window.customElements.define("onsite-search-bar", OnSiteSearchBar);
}

// ---------------------------------------------------------------------------------------------
// Filter - Range
// ---------------------------------------------------------------------------------------------
class FilterRange extends HTMLElement {
  static _globalInitialized = false;

  _getVals() {
    return this.checkboxes
      .map((c) => parseFloat(c.value))
      .sort((a, b) => a - b);
  }
  _snap(raw, isMin, opposite, vals, step = 1) {
    const floor = (v) => Math.floor(v / step) * step;
    const ceil = (v) => Math.ceil(v / step) * step;
    if (isMin) {
      let v = floor(raw);
      const thr = vals.filter((x) => x <= opposite).pop();
      return thr != null && raw > thr ? floor(thr) : v;
    } else {
      let v = ceil(raw);
      const thr = vals.filter((x) => x >= opposite)[0];
      return thr != null && raw < thr ? ceil(thr) : v;
    }
  }

  // Initialize shared state from currently rendered active facets
  static _initGlobalStore() {
    if (FilterRange._globalInitialized) return;
    FilterRange._globalInitialized = true;

    window._filterStore = window._filterStore || {};
    document
      .querySelectorAll(".removable-facet[data-group-name]")
      .forEach((facetEl) => {
        const group = facetEl.dataset.groupName;
        const minEl = facetEl.querySelector(".facet-range-min");
        const maxEl = facetEl.querySelector(".facet-range-max");
        if (!minEl || !maxEl) return;
        const min = parseFloat(minEl.textContent);
        const max = parseFloat(maxEl.textContent);
        window._filterStore[group] = window._filterStore[group] || {};
        // Persist current slider state across re-renders
        window._filterStore[group].slider = { min, max };
        window._filterStore[group].isFullRange = false;
      });
  }

  connectedCallback() {
    this.abortController = new AbortController();
    FilterRange._initGlobalStore();

    this.group = this.dataset.groupName;
    this.isDesktop = !!this.closest(".collection__top-bar");
    this.sliderEl = this.querySelector(".filter-range__slider");
    this.minR = this.sliderEl.querySelector("input[type=range]:first-child");
    this.maxR = this.sliderEl.querySelector("input[type=range]:last-child");
    this.minN = this.querySelector(".filter-range__num-min");
    this.maxN = this.querySelector(".filter-range__num-max");
    const form = this.closest('form[is="facet-form"]');
    this.checkboxes = Array.from(
      form.querySelectorAll(
        `.filter-range__values[data-group-name="${this.group}"] input[type=checkbox]`
      )
    );

    window._filterStore = window._filterStore || {};
    if (!window._filterStore[this.group]) {
      window._filterStore[this.group] = {
        slider: {
          min: parseFloat(this.minR.min),
          max: parseFloat(this.maxR.max),
        },
        facet: { min: null, max: null },
        isFullRange: true
      };
    }

    let slider = window._filterStore[this.group]?.slider;
    const storedIsFullRange = window._filterStore[this.group]?.isFullRange;

    // If we previously had a full range, re-sync to the current Liquid-provided limits
    // (context can change, e.g. on variant / facet re-render)
    if (storedIsFullRange) {
      slider = {
        min: parseFloat(this.minR.value),
        max: parseFloat(this.maxR.value)
      };
    }

    if (slider?.min != null && slider?.max != null) {
      this._setValues(slider.min, slider.max, true, false);
    } else {
      const s = window._filterStore[this.group].slider;
      this._setValues(s.min, s.max, false, false);
    }

    const facetData = window._filterStore[this.group].facet;
    if (facetData?.min != null && facetData?.max != null) {
      this._updateActiveFacets(facetData.min, facetData.max);
    }

    this.minLimit = parseFloat(this.minR.min);
    this.maxLimit = parseFloat(this.maxR.max);
    const signal = this.abortController.signal;
    this.minR.addEventListener("input", (e) => this._onInput(e), { signal });
    this.maxR.addEventListener("input", (e) => this._onInput(e), { signal });
    this.minN.addEventListener("blur", (e) => this._onBlur(e), { signal });
    this.maxN.addEventListener("blur", (e) => this._onBlur(e), { signal });
    this.minN.addEventListener("keydown", (e) => this._onInputKeyDown(e), { signal });
    this.maxN.addEventListener("keydown", (e) => this._onInputKeyDown(e), { signal });

    form.addEventListener("change", () => this._onFormUpdate(), { signal });
    form.addEventListener("submit", () => this._onFormUpdate(), { signal });

    document.addEventListener("facet:update", () => {
      const params = new URLSearchParams(window.location.search);
      if (!params.has(this.group)) {
        const defMin = parseFloat(this.minR.min),
          defMax = parseFloat(this.maxR.max);
        this._setValues(defMin, defMax, true, false);
      } else {
        const s2 = window._filterStore[this.group].slider;
        this._setValues(s2.min, s2.max, false, false);
      }
    }, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  _onInput(e) {
    const vals = this._getVals();
    let rawMin = parseFloat(this.minR.value),
      rawMax = parseFloat(this.maxR.value);

    // Enforce a minimum distance of 1 between min/max
    if (e.target === this.minR) {
      rawMin = Math.min(rawMin, rawMax - 1);
      this.minR.value = rawMin;
    } else if (e.target === this.maxR) {
      rawMax = Math.max(rawMax, rawMin + 1);
      this.maxR.value = rawMax;
    }

    const stepVal = parseFloat(e.target.step) || 1;
    const min =
      e.target === this.minR
        ? this._snap(rawMin, true, rawMax, vals, stepVal)
        : rawMin;
    const max =
      e.target === this.maxR
        ? this._snap(rawMax, false, rawMin, vals, stepVal)
        : rawMax;

    const inRange = vals.filter((v) => v >= min && v <= max);
    if (inRange.length === 0) {
      if (e.target === this.minN || e.target === this.minR) {
        const cand = vals.filter((v) => v <= max);
        min = cand.length ? cand[0] : min;
      } else {
        const cand = vals.filter((v) => v >= min);
        max = cand.length ? cand[cand.length - 1] : max;
      }
    }
    this._setValues(min, max, true, false);
  }

  _onInputKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      this._onBlur(e);
    }
  }

  _onBlur(e) {
    if (e.target.value === "") {
      if (e.target === this.minN) e.target.value = this.minR.min;
      else e.target.value = this.maxR.max;
    }

    const vals = this._getVals();
    let min = Math.max(
      this.minLimit,
      Math.min(parseFloat(this.minN.value), parseFloat(this.maxN.value))
    );
    let max = Math.min(
      this.maxLimit,
      Math.max(parseFloat(this.minN.value), parseFloat(this.maxN.value))
    );

    const stepVal = parseFloat(e.target.step) || 1;
    if (e.target === this.minN) min = this._snap(min, true, max, vals, stepVal);
    else max = this._snap(max, false, min, vals, stepVal);

    // If no checkbox values fall into the range, snap to the next available one
    const inRange = vals.filter((v) => v >= min && v <= max);
    if (inRange.length === 0) {
      if (e.target === this.minN) {
        const cand = vals.filter((v) => v <= max);
        min = cand.length ? cand[0] : min;
      } else {
        const cand = vals.filter((v) => v >= min);
        max = cand.length ? cand[cand.length - 1] : max;
      }
    }

    this._setValues(min, max, true, true);
    this._dispatchFormChange();
  }

  _setValues(min, max, updateStore, updateFacets = false) {
    // Safety check for early lifecycle calls
    if (!this.minR || !this.maxR || !this.minN || !this.maxN) {
      console.warn("FilterRange: DOM elements not yet initialized");
      return;
    }

    this.minR.value = this.minN.value = min;
    this.maxR.value = this.maxN.value = max;

    const full = parseFloat(this.maxR.max) - parseFloat(this.minR.min);
    const pMin = ((min - parseFloat(this.minR.min)) / full) * 100 + "%";
    const pMax = ((max - parseFloat(this.minR.min)) / full) * 100 + "%";
    this.sliderEl.style.setProperty("--range-min", pMin);
    this.sliderEl.style.setProperty("--range-max", pMax);
    
    // Determine "full range" vs filtered range using the actually available checkbox values,
    // not the slider input min/max (which can differ from the rendered values).
    const vals = this._getVals();
    let isFullRange = true;

    if (vals.length > 0) {
      const firstVal = vals[0];
      const lastVal = vals[vals.length - 1];
      
      isFullRange = (min <= firstVal && max >= lastVal);
    }

    if (isFullRange) {
      this.checkboxes.forEach((chk) => (chk.checked = false));
    } else {
      this.checkboxes.forEach((chk) => {
        const v = parseFloat(chk.value);
        const should = v >= min && v <= max;
        if (chk.checked !== should) chk.checked = should;
      });
    }

    if (updateFacets) {
      document
        .querySelectorAll(`.removable-facet[data-group-name="${this.group}"]`)
        .forEach((f) => {
          f.querySelector(".facet-range-min").textContent = min;
          f.querySelector(".facet-range-max").textContent = max;
        });
    }
    if (updateStore) {
      window._filterStore[this.group].slider = { min, max };
      window._filterStore[this.group].isFullRange = isFullRange;
    }
  }

  _onFormUpdate() {
    const { min, max } = window._filterStore[this.group].slider;
    window._filterStore[this.group].facet = { min, max };
    this._updateActiveFacets(min, max);
  }

  _updateActiveFacets(min, max) {
    document
      .querySelectorAll(`.removable-facet[data-group-name="${this.group}"]`)
      .forEach((f) => {
        f.querySelector(".facet-range-min").textContent = min;
        f.querySelector(".facet-range-max").textContent = max;
      });
  }

  _dispatchFormChange() {
    const form = this.closest('form[is="facet-form"]');
    form?.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
if (!window.customElements.get("filter-range")) {
  window.customElements.define("filter-range", FilterRange);
}

// Number counter: triggers animation when element enters the viewport
class NumberCounter extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._fallbackCheck = null;
    this._animationFrame = null;
    this._hasAnimated = false;
    this._onIntersect = this._onIntersect.bind(this);
  }

  connectedCallback() {
    this._numberEl = this.querySelector(".counter__number");

    if (!this._numberEl) {
      return;
    }

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      this._setToEndValue();
      this._hasAnimated = true;
      return;
    }

    this._setToStartValue();

    if ("IntersectionObserver" in window) {
      this._observer = new IntersectionObserver(this._onIntersect, {
        threshold: 0.1,
      });
      this._observer.observe(this);
    } else {
      const check = () => {
        if (this._hasAnimated) {
          return;
        }

        const rect = this.getBoundingClientRect();
        const triggerPoint = window.innerHeight * 0.9;

        if (rect.top < triggerPoint && rect.bottom >= 0) {
          this._startCounting();
          window.removeEventListener("scroll", check);
          window.removeEventListener("resize", check);
          this._fallbackCheck = null;
        }
      };

      check();
      window.addEventListener("scroll", check, { passive: true });
      window.addEventListener("resize", check);
      this._fallbackCheck = check;
    }
  }

  _onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.intersectionRatio > 0) {
        this._startCounting();
        if (this._observer) {
          this._observer.unobserve(this);
          this._observer.disconnect();
          this._observer = null;
        }
      }
    });
  }

  _startCounting() {
    if (this._hasAnimated || !this._numberEl) {
      return;
    }

    const { start, end, decimals } = this._getCounterValues();

    if (start === end) {
      this._numberEl.textContent = this._formatNumber(end, decimals);
      this._hasAnimated = true;
      return;
    }

    const duration = 3000;
    const startTime = performance.now();
    const range = end - start;

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;
      const current = start + range * eased;

      this._numberEl.textContent = this._formatNumber(current, decimals);

      if (progress < 1) {
        this._animationFrame = requestAnimationFrame(tick);
      } else {
        this._numberEl.textContent = this._formatNumber(end, decimals);
        this._animationFrame = null;
      }
    };

    this._hasAnimated = true;
    this._animationFrame = requestAnimationFrame(tick);
  }

  _setToStartValue() {
    const { start, decimals } = this._getCounterValues();
    this._numberEl.textContent = this._formatNumber(start, decimals);
  }

  _setToEndValue() {
    const { end, decimals } = this._getCounterValues();
    this._numberEl.textContent = this._formatNumber(end, decimals);
  }

  _getCounterValues() {
    const startRaw =
      this._numberEl.dataset.counterStart ?? this._numberEl.textContent ?? "0";
    const endRaw = this._numberEl.dataset.counterEnd ?? startRaw;
    const start = parseFloat(startRaw) || 0;
    const end = parseFloat(endRaw) || 0;
    const decimals = Math.max(
      this._decimalPlaces(startRaw),
      this._decimalPlaces(endRaw)
    );
    return { start, end, decimals };
  }

  _decimalPlaces(value) {
    const str = String(value);
    const [, fraction = ""] = str.split(".");
    return fraction.length;
  }

  _formatNumber(value, decimals) {
    if (decimals > 0) {
      const factor = Math.pow(10, decimals);
      return (Math.round(value * factor) / factor).toFixed(decimals);
    }
    return Math.round(value).toString();
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._fallbackCheck) {
      window.removeEventListener("scroll", this._fallbackCheck);
      window.removeEventListener("resize", this._fallbackCheck);
      this._fallbackCheck = null;
    }
    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }
  }
}

if (!window.customElements.get("number-counter")) {
  window.customElements.define("number-counter", NumberCounter);
}

// js/sections/threesixty.js
import { debounce, imageLoaded, generateSrcset } from "theme";
class ThreeSixtyInteractionManager {
  constructor(viewer) {
    this.viewer = viewer;
  }

  get isEnabled() {
    return this.viewer.getAttribute("data-interaction-enabled") === "true";
  }

  set isEnabled(value) {
    this.viewer.setAttribute(
      "data-interaction-enabled",
      value ? "true" : "false"
    );
  }

  toggle() {
    this.isEnabled = !this.isEnabled;
    this._updateButtonUI();
    return this.isEnabled;
  }

  updateForContext(width) {
    const { state, config } = this.viewer;
    const isPhotoSwipe = this.viewer.dataset.context === "photoswipe";
    const isMobile = width < 1000;

    if (isPhotoSwipe && !this.isEnabled) {
      this.isEnabled = true;
      state.isManuallyActivated = true;
      return;
    }

    if (isMobile) {
      this._handleMobile();
    } else {
      this._handleDesktop();
    }

    this._updateButtonVisibility(isMobile);
  }

  _handleMobile() {
    const { state, config } = this.viewer;
    const shouldEnable =
      config.draggableSetting && state.isManuallyActivated && !this.isEnabled;
    const shouldDisable =
      (!config.draggableSetting || !state.isManuallyActivated) &&
      this.isEnabled;

    if (shouldEnable) this.isEnabled = true;
    else if (shouldDisable) this.isEnabled = false;
  }

  _handleDesktop() {
    const { state } = this.viewer;

    if (state.isDesktopCarousel) {
      if (state.isManuallyActivated && !this.isEnabled) this.isEnabled = true;
      else if (!state.isManuallyActivated && this.isEnabled)
        this.isEnabled = false;
    } else if (!this.isEnabled) {
      this.isEnabled = true;
      state.isManuallyActivated = true;
    }
  }

  _updateButtonVisibility(isMobile) {
    const btn = this.viewer.activateButton;
    if (!btn) return;

    const { state, config } = this.viewer;
    const shouldShow = isMobile
      ? config.draggableSetting
      : state.isDesktopCarousel;
    btn.classList.toggle("md:hidden", !shouldShow);
  }

  _updateButtonUI() {
    const btn = this.viewer.activateButton;
    if (!btn) return;

    const labelEl = btn.querySelector(".threesixty-activate-label");
    if (!labelEl) return;

    const isActive = this.isEnabled;
    labelEl.textContent =
      btn.dataset[isActive ? "deactivateText" : "activateText"];
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  }
}

class ThreeSixtyViewer extends HTMLElement {
  static BREAKPOINTS = { MOBILE: 1000 };
  static THRESHOLDS = { DRAG: 16, GESTURE: 8 };
  static TIMINGS = {
    ANIMATION: 200,
    RESIZE_DEBOUNCE: 150,
    STATUS_UPDATE_DEBOUNCE: 300,
  };

  constructor() {
    super();

    this.state = {
      isDesktopCarousel: false,
      isManuallyActivated: false,
      isAnimating: false,
      isDragging: false,
      hasAutoplayed: false,
      isLoaded: false,
      wasDragged: false,
      touchMoveDetected: false,
    };

    this.config = { speed: 200, autoplayRotations: 1, draggableSetting: true };
    this.frames = { current: 0, total: 0, lastVisible: undefined };
    this.autoplayAnimationId = null;
    this._abortController = null;
    this._dragAbortController = null;

    this._initShadowDOM();
    this.imageElements = [];
    this.interactionManager = new ThreeSixtyInteractionManager(this);
  }

  _initShadowDOM() {
    this.attachShadow({ mode: "open" });
    this.shadowRoot.innerHTML = `
      <style>
        :host{display:block;position:relative;user-select:none;-webkit-user-select:none}:host([data-interaction-enabled="true"][loaded="true"]) .image-container{touch-action:pan-y pinch-zoom;cursor:grab}:host([data-interaction-enabled="true"][loaded="true"]) .image-container.active{cursor:grabbing}.image-container{width:100%;height:100%;position:relative;user-select:none;opacity:.66;transition:opacity .25s ease-in-out}:host([loaded="true"]) .image-container{opacity:1}:host([data-instant-load]) .image-container{opacity:1;transition:none}.image-container>img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;opacity:0;visibility:hidden;user-select:none;-webkit-user-drag:none;pointer-events:none;will-change:opacity}.image-container>img.is-visible{opacity:1;visibility:visible}
      </style>
      <slot></slot>
      <div class="image-container"></div>
    `;
    this.imageContainerEl = this.shadowRoot.querySelector(".image-container");
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this._parseConfig();
    this._initARIA();
    this._cacheElements();
    this._setupActivateButton();
    this._detectContext();
    this._setupObservers();
    this._addListeners();
    this.tabIndex = 0;
    this._preloadFirstImage();
    this._scheduleSpinImagesLoad();
    this.interactionManager.updateForContext(window.innerWidth);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this.stopAutoplay();
    this._resizeObserver?.disconnect();
    this._intersectionObserver?.disconnect();
  }

  _parseConfig() {
    const { speed, autoplay, interactionEnabled } = this.dataset;
    if (speed) this.config.speed = parseInt(speed, 10);
    if (autoplay) this.config.autoplayRotations = parseInt(autoplay, 10);
    this.config.draggableSetting = interactionEnabled === "true";
  }

  _initARIA() {
    this.setAttribute("role", "application");
    this.setAttribute("aria-label", "360° Product View");
    this.setAttribute("aria-description", "Use arrow keys to rotate and navigate through 360-degree product views. Drag or swipe to manually rotate.");
    this.setAttribute("aria-busy", "true");
    if (!this.hasAttribute("data-interaction-enabled")) {
      this.setAttribute("data-interaction-enabled", "false");
    }
  }

  _cacheElements() {
    this.statusEl = this.querySelector('[role="status"]');
    this.loaderEl = this.querySelector("pill-loader");
    this.activateButton = this.querySelector("#threesixty-activate-button");
    this.loaderEl?.setAttribute("aria-busy", "true");
  }

  _detectContext() {
    const gallery = this.closest("product-gallery");
    this.state.isDesktopCarousel =
      gallery?.parentElement?.classList.contains(
        "product-gallery--desktop-carousel"
      ) || false;
  }

  _setupActivateButton() {
    if (!this.activateButton) return;
    this.activateButton.addEventListener("click", this._onActivateButtonClick, {
      signal: this._abortController.signal,
    });
    if (this.dataset.context === "photoswipe") {
      this.activateButton.style.display = "none";
    }
  }

  _setupObservers() {
    this._resizeObserver = new ResizeObserver(
      debounce(
        () => this.interactionManager.updateForContext(window.innerWidth),
        ThreeSixtyViewer.TIMINGS.RESIZE_DEBOUNCE
      )
    );
    this._resizeObserver.observe(this);

    if (this.config.autoplayRotations > 0) {
      this._intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !this.state.hasAutoplayed) {
            this.state.hasAutoplayed = true;
            if (this.state.isLoaded) {
              this.autoplay(this.config.autoplayRotations);
            } else {
              this.addEventListener(
                "load-complete",
                () => this.autoplay(this.config.autoplayRotations),
                { once: true }
              );
            }
            this._intersectionObserver.unobserve(this);
          }
        },
        { threshold: 0.5 }
      );
      this._intersectionObserver.observe(this);
    }
  }

  _addListeners() {
    const { signal } = this._abortController;
    this.addEventListener("keydown", this._onKeyDown, { signal });

    if (this.imageContainerEl) {
      this.imageContainerEl.addEventListener("click", this._onClick, {
        signal,
      });
      this.imageContainerEl.addEventListener("mousedown", this._onDragStart, {
        signal,
      });
      this.imageContainerEl.addEventListener("touchstart", this._onDragStart, {
        passive: false,
        signal,
      });
    }

    const form = this.closest("product-form")?.querySelector("form");
    if (form) {
      form.addEventListener("product:rerender", this._onProductRerender, {
        signal,
      });
    }
  }

  // Public API
  autoplay(rotations = 1) {
    if (!this.state.isLoaded || this.frames.total === 0) return;

    this.stopAutoplay();
    const totalFrames = this.frames.total * rotations;
    const intervalMs = (this.config.speed * 10) / this.frames.total;
    let framesPlayed = 0;
    let lastFrameTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - lastFrameTime;
      if (elapsed >= intervalMs) {
        this._navigateFrame(-1);
        framesPlayed++;
        lastFrameTime = currentTime - (elapsed % intervalMs);
        if (framesPlayed >= totalFrames) return this.stopAutoplay();
      }
      this.autoplayAnimationId = requestAnimationFrame(animate);
    };

    this.autoplayAnimationId = requestAnimationFrame(animate);
  }

  stopAutoplay() {
    if (this.autoplayAnimationId) {
      cancelAnimationFrame(this.autoplayAnimationId);
      this.autoplayAnimationId = null;
    }
  }

  // Image Loading
  _scheduleSpinImagesLoad() {
    if (this.dataset.context === "photoswipe") {
      this._preloadSpinImages();
    } else {
      this.addEventListener(
        "threesixty:first-loaded",
        () => this._preloadSpinImages(),
        { once: true }
      );
    }
  }

  async _preloadFirstImage() {
    const imageData = this._parseImageData();
    if (!imageData) return;

    const { sizes, baseImageUrls, widths } = imageData;
    this.frames.total = baseImageUrls.length;
    this.imageContainerEl.innerHTML = "";
    this.imageElements = [];

    const initialFrame = this._getInitialFrame();

    const initialUrl = baseImageUrls[initialFrame].replace(/&amp;/g, "&");
    const initialImg = this._createImage(initialUrl, widths, sizes, true);
    this.imageElements[initialFrame] = initialImg;
    this.imageContainerEl.appendChild(initialImg);

    try {
      const loadStartTime = performance.now();
      await imageLoaded(initialImg);
      const loadDuration = performance.now() - loadStartTime;
      const wasCached = loadDuration < 100;

      this._showFrame(initialFrame);

      if (wasCached) {
        this.setAttribute("loaded", "first");
        this.setAttribute("data-instant-load", "true");
      } else {
        this.setAttribute("loaded", "first");
      }

      this.dispatchEvent(new CustomEvent("threesixty:first-loaded"));
    } catch (error) {
      console.warn("ThreeSixtyViewer: Error loading initial frame", error);
    }
  }

  async _preloadSpinImages() {
    const imageData = this._parseImageData();
    if (!imageData) return;

    const { sizes, baseImageUrls, widths } = imageData;
    const initialFrame = this._getInitialFrame();

    const remainingImages = baseImageUrls
      .map((url, index) => {
        if (index === initialFrame) return null;

        const img = this._createImage(
          url.replace(/&amp;/g, "&"),
          widths,
          sizes,
          false
        );
        this.imageElements[index] = img;
        this.imageContainerEl.appendChild(img);
        return img;
      })
      .filter(Boolean);

    try {
      const loadStartTime = performance.now();
      await imageLoaded(remainingImages);
      const loadDuration = performance.now() - loadStartTime;
      const allCached = loadDuration < 100;

      this._setLoadingState(false);

      if (allCached && this.hasAttribute("data-instant-load")) {
        this.removeAttribute("data-instant-load");
      }

      this._announceStatus(
        `360° view loaded. ${this.frames.total} frames available. Use arrow keys to navigate.`
      );
      this.dispatchEvent(new CustomEvent("load-complete"));
    } catch (error) {
      console.error(
        "ThreeSixtyViewer: Error preloading remaining frames.",
        error
      );
      this._setLoadingState(false);
      this.loaderEl && (this.loaderEl.textContent = "Error loading images.");
      this._announceStatus("Error loading 360° view images.");
    }
  }

  _getInitialFrame() {
    const attr = this.getAttribute("data-current-frame");
    const frame = attr !== null ? parseInt(attr, 10) : 0;
    const valid = Math.max(0, Math.min(frame, this.frames.total - 1));
    if (attr === null) this.setAttribute("data-current-frame", "0");
    return valid;
  }

  _parseImageData() {
    try {
      const sizes = this.dataset.sizes;
      const baseImageUrls = JSON.parse(
        this.dataset.images.replace(/&quot;/g, '"')
      );
      const widths = JSON.parse(this.dataset.widths);

      if (!sizes || !baseImageUrls.length || !widths.length) {
        throw new Error("Missing required data");
      }

      return { sizes, baseImageUrls, widths };
    } catch (e) {
      console.error("ThreeSixtyViewer: Failed to parse image data.", e);
      this.loaderEl && (this.loaderEl.textContent = "Error loading data.");
      return null;
    }
  }

  _createImage(url, widths, sizes, isEager) {
    const img = document.createElement("img");
    const urlObj = new URL(url, document.baseURI);
    urlObj.searchParams.set("width", widths[0]);

    img.src = urlObj.toString();
    img.srcset = generateSrcset(url, widths);
    img.sizes = sizes;
    img.alt = "";
    img.setAttribute("aria-hidden", "true");
    img.loading = isEager ? "eager" : "lazy";
    img.fetchPriority = isEager ? "high" : "low";
    img.draggable = false;
    img.decoding = "sync";
    return img;
  }

  _setLoadingState(loading) {
    this.state.isLoaded = !loading;
    const busyState = String(loading);
    this.setAttribute("aria-busy", busyState);
    this.loaderEl?.setAttribute("aria-busy", busyState);
    if (!loading) this.setAttribute("loaded", "true");
  }

  // Frame Navigation
  _showFrame(index) {
    if (!this.imageElements.length) return;

    const newIndex = (index + this.frames.total) % this.frames.total;
    if (
      this.frames.current === newIndex &&
      this.imageElements[newIndex].classList.contains("is-visible")
    )
      return;

    if (
      this.frames.lastVisible !== undefined &&
      this.frames.lastVisible !== newIndex
    ) {
      this.imageElements[this.frames.lastVisible].classList.remove(
        "is-visible"
      );
    }
    this.imageElements[newIndex].classList.add("is-visible");
    this.frames.lastVisible = this.frames.current = newIndex;
    this.setAttribute("data-current-frame", String(newIndex));
    this._announceFrameChange(newIndex);
    this.dispatchEvent(
      new CustomEvent("frame-change", { detail: { newFrame: newIndex } })
    );
  }

  _navigateFrame(direction) {
    if (this.frames.total === 0) return;
    this.frames.current =
      (this.frames.current + direction + this.frames.total) % this.frames.total;
    this._showFrame(this.frames.current);
  }

  _announceFrameChange(index) {
    if (!this.statusEl || !this.state.isLoaded) return;

    if (!this._debouncedStatusUpdate) {
      this._debouncedStatusUpdate = debounce(
        (i) => this._announceStatus(`Frame ${i + 1} of ${this.frames.total}`),
        ThreeSixtyViewer.TIMINGS.STATUS_UPDATE_DEBOUNCE
      );
    }
    this._debouncedStatusUpdate(index);
  }

  _announceStatus(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  }

  // Event Handlers
  _onActivateButtonClick = async () => {
    if (this.state.isAnimating || !this.state.isLoaded) return;

    this.state.isAnimating = true;
    const contentEl = this.activateButton?.querySelector(
      ".threesixty-activate-button-content"
    );
    if (!contentEl) return (this.state.isAnimating = false);

    const duration = ThreeSixtyViewer.TIMINGS.ANIMATION;
    const slideOut = [
      { transform: "translateY(0)", opacity: 1 },
      { transform: "translateY(-10px)", opacity: 0 },
    ];
    const slideIn = [
      { transform: "translateY(10px)", opacity: 0 },
      { transform: "translateY(0)", opacity: 1 },
    ];

    await contentEl.animate(slideOut, {
      duration,
      easing: "ease-out",
      fill: "forwards",
    }).finished;

    this.state.isManuallyActivated = !this.state.isManuallyActivated;
    this.interactionManager.toggle();

    await contentEl.animate(slideIn, {
      duration,
      easing: "ease-out",
      fill: "forwards",
    }).finished;
    contentEl.style.cssText = "";
    this.state.isAnimating = false;

    this.dispatchEvent(
      new CustomEvent("threesixty:dragging-changed", {
        bubbles: true,
        detail: { isDraggingEnabled: this.interactionManager.isEnabled },
      })
    );
  };

  _onKeyDown = (e) => {
    if (!this.state.isLoaded) return e.preventDefault();

    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      this.stopAutoplay();
      this._navigateFrame(e.key === "ArrowLeft" ? 1 : -1);
    }
  };

  _onClick = (e) => {
    if (this.state.wasDragged) {
      e.preventDefault();
      e.stopPropagation();
      this.state.wasDragged = false;
      return;
    }
    this.dispatchEvent(
      new CustomEvent("viewer:click", {
        bubbles: true,
        detail: { originalEvent: e, currentFrame: this.frames.current },
      })
    );
  };

  _onDragStart = (e) => {
    if (!this.interactionManager.isEnabled || !this.state.isLoaded) return;
    if (e.type === "touchstart" && e.touches.length > 1) return;

    if (e.type === "mousedown") {
      e.preventDefault();
      e.stopPropagation();
    }

    this.stopAutoplay();
    this.state.wasDragged = false;
    this.state.isDragging = true;

    if (e.type === "mousedown") {
      this.dragStartX = e.clientX;
      document.body.style.cssText = "cursor: grabbing; user-select: none;";
      this.imageContainerEl.classList.add("active");
    } else {
      this.dragStartX = e.touches[0].clientX;
      this.dragStartY = e.touches[0].clientY;
      this.state.touchMoveDetected = false;
    }

    // Setup AbortController for drag events
    this._dragAbortController = new AbortController();
    const { signal } = this._dragAbortController;

    window.addEventListener("mousemove", this._onDragMove, { signal });
    window.addEventListener("mouseup", this._onDragEnd, { signal });
    window.addEventListener("touchmove", this._onDragMove, {
      passive: false,
      signal,
    });
    window.addEventListener("touchend", this._onDragEnd, { signal });
  };

  _onDragMove = (e) => {
    if (!this.state.isDragging) return;

    let currentX, currentY;

    if (e.type === "mousemove") {
      currentX = e.clientX;
      e.preventDefault();
      e.stopPropagation();
    } else {
      if (e.touches.length > 1) return this._cleanupDrag();

      currentX = e.touches[0].clientX;
      currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - this.dragStartX);
      const deltaY = Math.abs(currentY - this.dragStartY);

      if (
        !this.state.touchMoveDetected &&
        (deltaX > ThreeSixtyViewer.THRESHOLDS.GESTURE ||
          deltaY > ThreeSixtyViewer.THRESHOLDS.GESTURE)
      ) {
        this.state.touchMoveDetected = true;
        if (deltaX <= deltaY) return this._cleanupDrag();
      }

      if (this.state.touchMoveDetected && deltaX > deltaY) {
        e.preventDefault();
        e.stopPropagation();
      } else if (!this.state.touchMoveDetected) {
        return;
      }
    }

    const deltaX = currentX - this.dragStartX;
    if (Math.abs(deltaX) > ThreeSixtyViewer.THRESHOLDS.DRAG) {
      this.state.wasDragged = true;
      this._navigateFrame(deltaX > 0 ? -1 : 1);
      this.dragStartX = currentX;
    }
  };

  _onDragEnd = () => this._cleanupDrag();

  _cleanupDrag() {
    this._dragAbortController?.abort();
    this._dragAbortController = null;

    this.state.isDragging = false;
    document.body.style.cssText = "";
    this.imageContainerEl?.classList.remove("active");
  }
}

if (!window.customElements.get("threesixty-viewer")) {
  window.customElements.define("threesixty-viewer", ThreeSixtyViewer);
}

// Cart Update Button Component - extends CustomButton for theme animations
class CartUpdateButton extends CustomButton {
  constructor() {
    super();
    this.addEventListener('click', this._onClick.bind(this));
  }

  async _onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const updatesData = this.getAttribute('data-updates');
    
    this.setAttribute('aria-busy', 'true');
    this.disabled = true;
    
    try {
      const updates = {};
      updatesData.split('|').forEach(item => {
        const lastColonIndex = item.lastIndexOf(':');
        const key = item.substring(0, lastColonIndex);
        const qty = item.substring(lastColonIndex + 1);
        updates[key] = parseInt(qty, 10);
      });
      
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        const cartData = await response.json();
        
        if (window.themeVariables.settings.pageType === 'cart') {
          window.location.href = window.location.pathname + '?updated=' + Date.now();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } else {
        throw new Error('Cart update failed');
      }
    } catch (error) {
      console.error('Error in cart update:', error);
      this.removeAttribute('aria-busy');
      this.disabled = false;
    }
  }
}

if (!window.customElements.get('cart-update-button')) {
  window.customElements.define('cart-update-button', CartUpdateButton, { extends: 'button' });
}

// ORTLIEB: Image lightbox - click any image to open PhotoSwipe with bottom bar navigation
class ImageLightbox extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._images = [...this.querySelectorAll('img')];

    if (!this._images.length) return;

    const signal = this._abortController.signal;
    this._images.forEach((img, i) => {
      img.addEventListener('click', () => this._openLightbox(i), { signal });
    });

    // Intercept product-zoom-button clicks in capture phase — before ProductZoomButton's own
    // handler runs, which would error trying to find .product-gallery__media-list-wrapper.
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[is="product-zoom-button"]');
      if (!btn) return;
      e.stopPropagation();
      const index = this._images.findIndex(img => img.parentElement?.contains(btn));
      this._openLightbox(index >= 0 ? index : 0);
    }, { signal, capture: true });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._lightbox?.destroy();
    this._lightbox = null;
  }

  async _openLightbox(startIndex = 0) {
    if (!this._lightbox) {
      const { PhotoSwipeLightbox } = await import('vendor');
      const hasMultiple = this._images.length > 1;

      this._lightbox = new PhotoSwipeLightbox({
        pswpModule: () => import('photoswipe'),
        bgOpacity: 1,
        maxZoomLevel: 3,
        closeTitle: window.themeVariables.strings.closeGallery,
        zoomTitle: window.themeVariables.strings.zoomGallery,
        errorMsg: window.themeVariables.strings.errorGallery,
        arrowPrev: false,
        arrowNext: false,
        counter: false,
        zoom: false,
        clickToCloseNonZoomable: false,
        tapAction: false,
        doubleTapAction: false,
        closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
          <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
          <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
          <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
        </svg>`
      });

      this._lightbox.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement);

      if (hasMultiple) {
        this._lightbox.on('uiRegister', () => {
          this._lightbox.pswp.ui.registerElement({
            name: 'bottom-bar',
            order: 5,
            appendTo: 'wrapper',
            html: `<div class="pagination">
              <button class="pagination__item group" rel="prev">
                <span class="animated-arrow animated-arrow--reverse"></span>
              </button>
              <span class="pagination__current text-sm">
                <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
              </span>
              <button class="pagination__item group" rel="next">
                <span class="animated-arrow"></span>
              </button>
            </div>`,
            onInit: (el, pswp) => {
              el.querySelector('.pagination__page-count').innerText = pswp.getNumItems();
              el.querySelector('[rel="prev"]').addEventListener('click', () => pswp.prev());
              el.querySelector('[rel="next"]').addEventListener('click', () => pswp.next());
              pswp.on('change', () => {
                el.querySelector('.pagination__current-page').innerText = pswp.currIndex + 1;
              });
            }
          });
        });
      }

      this._lightbox.init();
    }

    const slides = this._images.map((img) => ({
      thumbnailElement: img,
      src: this._getHighResImageUrl(img.src),
      srcset: img.srcset,
      msrc: img.currentSrc || img.src,
      width: parseInt(img.getAttribute('width')) || 800,
      height: parseInt(img.getAttribute('height')) || 600,
      alt: img.alt,
      thumbCropped: true
    }));

    this._lightbox.loadAndOpen(startIndex, slides);
  }

  _getHighResImageUrl(src) {
    try {
      const url = new URL(src, document.baseURI);
      url.searchParams.set('width', '2000');
      return url.toString();
    } catch {
      return src;
    }
  }
}

if (!window.customElements.get('image-lightbox')) {
  window.customElements.define('image-lightbox', ImageLightbox);
}

// Buy Local Button Component - manages Outtra widget loading and fallback via CSS
class BuyLocalButton extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
  }

  connectedCallback() {
    const barcode = this.getAttribute('barcode');
    const lang = this.getAttribute('lang') || 'en';

    if (!barcode) {
      this.remove();
      return;
    }

    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._setupErrorHandler(signal);
    this._loadOuttraScript(barcode, lang);
  }

  _loadOuttraScript(barcode, lang) {
    if (document.querySelector('.outtra-button-script')) {
      return;
    }

    const script = document.createElement('script');
    script.className = 'outtra-button-script';
    script.src = 'https://brandwidgets.outtra.com/js/ortlieb/button-widget.js';
    script.setAttribute('data-outtrabrand_id', '193');
    script.setAttribute('data-gtins', barcode);
    script.setAttribute('data-lang', lang);
    script.async = true;

    this.appendChild(script);
  }

  _setupErrorHandler(signal) {
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      
      if (reason?.config?.url?.includes('/brand-api/ortlieb/button-widget')) {
        const status = reason?.response?.status;
        if (status && status >= 400) {
          console.warn('BuyLocalButton: Outtra API returned error status', status);
          this.style.display = 'none';
        }
      }
    }, { signal });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;
  }
}

if (!window.customElements.get('buy-local-button')) {
  window.customElements.define('buy-local-button', BuyLocalButton);
}

/* Comparison Table – Liquid-rendered column swapping via template cloning */
import { animate, stagger } from "vendor";

class ComparisonGrid extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const signalOptions = { signal: this.abortController.signal };

    this.stickySetupFrame = null;
    this.collapseScrollFrame = null;
    this.maxColumns = parseInt(this.dataset.maxColumns, 10) || 3;
    this.totalEntries = parseInt(this.dataset.totalEntries, 10) || 0;
    this.isDrawerLayout = this.dataset.layoutContext === 'drawer' || !!this.closest('.drawer');
    this.hasRevealedRows = this.dataset.rowsRevealed === 'true';
    this.smallQuery = window.matchMedia(window.themeVariables.breakpoints['sm-max']);
    this.midQuery = window.matchMedia(window.themeVariables.breakpoints['md-max']);
    this.syncViewportState();
    this.setupSwapPool();
    this.hydrateFromUrl();
    this.resolveVisibleDuplicates();
    this.initialKeys = [...this.currentKeys];

    [this.smallQuery, this.midQuery].forEach(mq => mq.addEventListener('change', () => {
      this.syncViewportState();
      this.resolveVisibleDuplicates();
      this.refreshGridState();
      if (this.stickyRowgroup) this.scheduleStickySetup();
    }, signalOptions));

    this.refreshGridState({ includeCollapsibleRows: false });
    document.addEventListener('listbox:change', this.onListboxChange.bind(this), signalOptions);

    this.stickyRowgroup = this.querySelector('.compare-grid__row-group--selectors');
    if (this.stickyRowgroup) this.setupStickyContext(signalOptions);

    this.setupCollapsibleRows(signalOptions);
    this.applyRestoredState();

    if (this.skipInitialRevealRows) {
      this.showRowsWithoutReveal();
    } else if (matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      inView(this, () => this.revealRows(), { margin: '0px 0px -50px 0px' });
    }
  }

  setupCollapsibleRows(signalOptions) {
    /* Collapsible-rows logic. Runs *after* syncContentRows so we count rows the
       user actually sees (syncContentRows hides rows whose cells have no value
       and re-runs on viewport changes). The toggle row + button are always
       rendered from Liquid when the threshold is set; we hide them when nothing
       is actually collapsed. Collapsing is purely CSS-driven via
       `data-collapsed-by-toggle` so the host's `is-expanded` class flips
       visibility without re-walking the DOM. */
    this.collapsedThreshold = parseInt(this.dataset.collapsedThreshold, 10) || 0;
    if (this.collapsedThreshold <= 0) return;

    this.collapsibleToggleRow = this.querySelector('.compare-grid__row--toggle');
    const button = this.querySelector('[data-action="toggle-collapsed-rows"]');
    if (!this.collapsibleToggleRow || !button) return;

    button.addEventListener('click', () => {
      const expanded = !this.classList.contains('is-expanded');
      const topBeforeCollapse = expanded ? null : button.getBoundingClientRect().top;
      this.setCollapsibleRowsExpanded(expanded);
      if (expanded) {
        const targets = [...this.querySelectorAll('[data-collapsed-by-toggle="true"][reveal-on-scroll="true"], [data-collapsed-by-toggle="true"] [reveal-on-scroll="true"]')];
        this.playRevealAnimation(targets);
      } else {
        this.stabilizeCollapseScroll(button, topBeforeCollapse);
      }
    }, signalOptions);

    this.applyCollapsibleRows();
  }

  setCollapsibleRowsExpanded(expanded) {
    this.classList.toggle('is-expanded', expanded);
    const button = this.querySelector('[data-action="toggle-collapsed-rows"]');
    if (!button) return;
    const label = button.querySelector('.compare-grid__toggle-text');
    if (label) label.textContent = expanded ? button.dataset.viewLess : button.dataset.viewMore;
    button.setAttribute('aria-expanded', String(expanded));
  }

  stabilizeCollapseScroll(button, topBeforeCollapse) {
    if (!button || typeof topBeforeCollapse !== 'number') return;

    const scrollContainer = this.getScrollContainer();
    const correctScroll = () => {
      const topAfterCollapse = button.getBoundingClientRect().top;
      const topDelta = topAfterCollapse - topBeforeCollapse;
      if (Math.abs(topDelta) > 1) {
        if (scrollContainer) scrollContainer.scrollBy({ top: topDelta, behavior: 'auto' });
        else window.scrollBy({ top: topDelta, behavior: 'auto' });
      }
      button.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    };

    correctScroll();
    cancelAnimationFrame(this.collapseScrollFrame);
    this.collapseScrollFrame = requestAnimationFrame(() => {
      this.collapseScrollFrame = null;
      correctScroll();
    });
  }

  getScrollContainer() {
    if (!this.isDrawerLayout) return null;
    return this.closest('x-drawer')?.shadowRoot?.querySelector('[part="body"]') || null;
  }

  captureState() {
    const keys = this.currentKeys?.length
      ? [...this.currentKeys]
      : [...this.querySelectorAll('[data-col-slot="selector"][data-col-index]')]
        .sort((a, b) => Number(a.dataset.colIndex) - Number(b.dataset.colIndex))
        .map(cell => cell.querySelector('input[type="hidden"]')?.value || null);
    const initialKeys = this.initialKeys || keys;
    const keysChanged = keys.some((key, index) => key !== initialKeys[index]);
    const expanded = this.classList.contains('is-expanded');
    const rowsRevealed = this.hasRevealedRows || this.dataset.rowsRevealed === 'true';
    if (!keysChanged && !expanded && !rowsRevealed) return null;
    return {
      keys: keysChanged ? keys : null,
      expanded,
      rowsRevealed
    };
  }

  restoreState(state) {
    if (!state) return;
    this.pendingRestoredState = state;
    if (this.isConnected && this.templates) this.applyRestoredState();
  }

  applyRestoredState() {
    const state = this.pendingRestoredState;
    if (!state) return;
    this.pendingRestoredState = null;
    this.skipInitialRevealRows = !!state.rowsRevealed;

    if (Array.isArray(state.keys)) {
      state.keys.slice(0, this.maxColumns).forEach((key, colIndex) => {
        if (!key || key === this.getColumnKey(colIndex) || !this.templates.has(key)) return;
        this.swapColumn(colIndex, key, { silent: true });
      });
      this.resolveVisibleDuplicates();
    }

    this.setCollapsibleRowsExpanded(!!state.expanded);
    this.refreshGridState();
    if (this.skipInitialRevealRows) this.showRowsWithoutReveal();
    if (this.stickyRowgroup) this.scheduleStickySetup();
  }

  refreshGridState({ includeCollapsibleRows = true } = {}) {
    this.syncContentRows();
    if (includeCollapsibleRows) this.applyCollapsibleRows();
    this.refreshAllOptions();
  }

  applyCollapsibleRows() {
    if (!this.collapsedThreshold || !this.collapsibleToggleRow) return;
    /* Walk only rows inside content rowgroups so structural rows (image,
       selector, price, swatches, CTA, footer) are never collapsed. Once
       `collapsedThreshold` non-hidden content rows have passed, every following
       row inside a content rowgroup (incl. headings) is marked collapsed so a
       user who hits the threshold mid-group doesn't see an orphaned heading. */
    const rows = this.querySelectorAll('.compare-grid__row-group--features .compare-grid__row');
    let visibleContentRows = 0;
    let collapsedAny = false;
    rows.forEach(row => {
      delete row.dataset.collapsedByToggle;
      if (visibleContentRows >= this.collapsedThreshold) {
        row.dataset.collapsedByToggle = 'true';
        collapsedAny = true;
        return;
      }
      if (row.dataset.dimensionRow === 'true' && !row.hidden) {
        visibleContentRows += 1;
      }
    });

    /* Collapse rowgroups whose every child row is either hidden by sync or
       collapsed by the toggle, so their margins/spacing also disappear. */
    this.querySelectorAll('.compare-grid__row-group--features').forEach(rowgroup => {
      delete rowgroup.dataset.collapsedByToggle;
      const childRows = rowgroup.querySelectorAll('.compare-grid__row');
      if (!childRows.length) return;
      const hasVisibleRow = [...childRows].some(row => !row.hidden && row.dataset.collapsedByToggle !== 'true');
      if (!hasVisibleRow) rowgroup.dataset.collapsedByToggle = 'true';
    });

    this.collapsibleToggleRow.hidden = !collapsedAny;
  }

  playRevealAnimation(targets) {
    /* Shared reveal animation used by initial scroll-into-view and by the
       collapsible toggle when rows are expanded. */
    if (!matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
    if (!targets || !targets.length) return;
    animate(
      targets,
      { opacity: [0, 1], transform: ['translateY(0.5rem)', 'translateY(0)'] },
      { duration: 0.3, delay: stagger(0.025), easing: 'ease' }
    );
  }

  setupSwapPool() {
    this.urlParamName = this.dataset.urlParamName || null;
    this.placeholderKey = this.dataset.placeholderKey || null;
    this.pool = this.querySelector('.compare-grid__template-pool');
    /* Pool element may be missing when no offset items exist; create one
       so initially-rendered columns can still be captured into templates. */
    if (!this.pool) {
      this.pool = document.createElement('div');
      this.pool.className = 'compare-grid__template-pool';
      this.pool.hidden = true;
      this.pool.setAttribute('aria-hidden', 'true');
      this.appendChild(this.pool);
    }
    this.templates = new Map();
    this.sectionId = this.dataset.sectionId || '';
    this.currentKeys = [];
    for (let i = 0; i < this.maxColumns; i++) {
      const cell = this.getColumnSelectorCell(i);
      this.currentKeys[i] = cell?.querySelector('input[type="hidden"]')?.value || null;
      const listbox = cell?.querySelector('[data-compare-select]');
      if (listbox) {
        listbox.dataset.gridSection = this.sectionId;
        listbox.dataset.colIndex = String(i);
      }
    }
    this.pool.querySelectorAll('template[data-compare-key]').forEach(tpl => {
      const key = tpl.dataset.compareKey;
      if (!key) return;
      this.templates.set(key, tpl);
      tpl.content.querySelectorAll('[data-compare-select]').forEach(lb => {
        lb.dataset.gridSection = this.sectionId;
      });
      [...tpl.content.children].forEach(child => this.resetCellState(child));
    });
    for (let i = 0; i < this.maxColumns; i++) {
      const key = this.currentKeys[i];
      if (!key || this.templates.has(key)) continue;
      this.captureColumnIntoPool(i, key);
    }
  }

  captureColumnIntoPool(colIndex, key) {
    const cells = [...this.querySelectorAll(`[data-col-slot][data-col-index="${colIndex}"]`)].map(cell => {
      const clone = cell.cloneNode(true);
      clone.removeAttribute('data-col-index');
      clone.removeAttribute('aria-colindex');
      this.resetCellState(clone);
      this.suffixIds(clone, `-pool-${key}`);
      return clone;
    });
    if (!cells.length) return;
    const tpl = document.createElement('template');
    tpl.dataset.compareKey = key;
    tpl.content.append(...cells);
    this.pool.appendChild(tpl);
    this.templates.set(key, tpl);
  }

  suffixIds(root, suffix) {
    if (!suffix) return;
    const idMap = new Map();
    root.querySelectorAll('[id]').forEach(el => {
      const oldId = el.id;
      const newId = `${oldId}${suffix}`;
      el.id = newId;
      idMap.set(oldId, newId);
    });
    if (!idMap.size) return;
    root.querySelectorAll('[aria-controls],[aria-owns]').forEach(el => {
      ['aria-controls', 'aria-owns'].forEach(attr => {
        if (!el.hasAttribute(attr)) return;
        const remapped = el.getAttribute(attr).split(/\s+/)
          .map(token => idMap.get(token) || token).join(' ');
        el.setAttribute(attr, remapped);
      });
    });
  }

  resetCellState(root) {
    root.querySelectorAll('x-popover[open], dialog-element[open], [open][role="dialog"]').forEach(p => p.removeAttribute('open'));
    root.querySelectorAll('[aria-expanded="true"]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    root.querySelectorAll('[data-compare-select]').forEach(listbox => {
      listbox.removeAttribute('aria-activedescendant');
      const hidden = listbox.querySelector('input[type="hidden"]');
      const currentOption = listbox.querySelector('button[role="option"].popover-listbox__option--current');
      const baselineHandle = currentOption?.value || hidden?.defaultValue || hidden?.value || null;
      if (hidden && baselineHandle != null) hidden.value = baselineHandle;
      const baselineTitle = currentOption?.getAttribute('title') || currentOption?.textContent?.trim() || '';
      listbox.querySelectorAll('button[role="option"]').forEach(opt => {
        opt.removeAttribute('id');
        opt.setAttribute('aria-selected', opt.classList.contains('popover-listbox__option--current') ? 'true' : 'false');
      });
      if (listbox.hasAttribute('aria-owns')) {
        listbox.getAttribute('aria-owns').split(/\s+/).forEach(id => {
          const target = id ? root.querySelector(`#${CSS.escape(id)}`) : null;
          if (target && baselineTitle) target.textContent = baselineTitle;
        });
      }
    });
  }

  hydrateFromUrl() {
    if (!this.urlParamName || !this.pool) return;
    const params = new URLSearchParams(location.search);
    const param = params.get(this.urlParamName);
    if (!param) return;
    const seen = new Set();
    const wanted = param.split(',').map(s => s.trim()).filter(key => {
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, this.maxColumns);
    wanted.forEach((key, colIndex) => {
      if (this.getColumnKey(colIndex) === key) return;
      if (!this.templates.has(key)) return;
      this.swapColumn(colIndex, key, { silent: true });
    });
    if (params.get('placeholder') !== 'true') return;
    if (!this.placeholderKey || !this.templates.has(this.placeholderKey)) return;
    for (let colIndex = wanted.length; colIndex < this.maxColumns; colIndex++) {
      if (this.getColumnKey(colIndex) === this.placeholderKey) continue;
      this.swapColumn(colIndex, this.placeholderKey, { silent: true });
    }
  }

  getColumnSelectorCell(colIndex) {
    return this.querySelector(`[data-col-slot="selector"][data-col-index="${colIndex}"]`);
  }

  getColumnKey(colIndex) {
    return this.currentKeys?.[colIndex] || null;
  }

  getVisibleKeys() {
    const keys = [];
    for (let i = 0; i < this.visibleColumns; i++) {
      const key = this.getColumnKey(i);
      if (key) keys.push(key);
    }
    return keys;
  }

  resolveVisibleDuplicates() {
    const usedKeys = new Set();

    for (let colIndex = 0; colIndex < this.visibleColumns; colIndex++) {
      const currentKey = this.getColumnKey(colIndex);
      if (!currentKey || currentKey === this.placeholderKey) continue;

      if (!usedKeys.has(currentKey)) {
        usedKeys.add(currentKey);
        continue;
      }

      const cell = this.getColumnSelectorCell(colIndex);
      const listbox = cell?.querySelector('[data-compare-select]');
      if (!listbox) continue;

      const replacementKey = [...listbox.querySelectorAll('button[role="option"]')]
        .map(option => option.value)
        .find(key => key && key !== this.placeholderKey && !usedKeys.has(key));

      if (!replacementKey || replacementKey === currentKey) continue;

      this.swapColumn(colIndex, replacementKey, { silent: true });
      usedKeys.add(replacementKey);
    }
  }

  onListboxChange(event) {
    const listbox = event.target.closest?.('[data-compare-select]');
    if (!listbox) return;
    if (listbox.dataset.gridSection !== this.sectionId) return;
    const colIndex = parseInt(listbox.dataset.colIndex, 10);
    if (Number.isNaN(colIndex)) return;
    const newKey = event.detail?.value;
    if (!newKey || newKey === this.getColumnKey(colIndex)) return;
    if (this.getVisibleKeys().some((key, i) => i !== colIndex && key === newKey)) {
      this.refreshAllOptions();
      return;
    }
    this.swapColumn(colIndex, newKey);
  }

  swapColumn(colIndex, newKey, { silent = false } = {}) {
    const tpl = this.templates.get(newKey);
    if (!tpl) return;

    const newCells = new Map();
    tpl.content.querySelectorAll('[data-col-slot]').forEach(c => newCells.set(c.dataset.colSlot, c));

    const colNumber = colIndex + 1;
    const oldSelector = this.getColumnSelectorCell(colIndex);
    const oldPopoverId = oldSelector?.querySelector('button[aria-controls]')?.getAttribute('aria-controls');
    const oldPopover = oldPopoverId ? document.getElementById(oldPopoverId) : null;
    let hidePromise = Promise.resolve();
    if (oldPopover) {
      if (oldPopover.parentElement !== document.body) document.body.appendChild(oldPopover);
      oldPopover._originalParentBeforeAppend = null;
      if (oldPopover.open) {
        hidePromise = Promise.resolve(oldPopover.hide()).catch(() => {});
      }
      hidePromise = hidePromise.then(() => oldPopover.remove());
    }

    this.querySelectorAll(`[data-col-slot][data-col-index="${colIndex}"]`).forEach(oldCell => {
      const slot = oldCell.dataset.colSlot;
      const sourceCell = newCells.get(slot);
      if (!sourceCell) return;
      const newCell = sourceCell.cloneNode(true);
      newCell.setAttribute('data-col-index', String(colIndex));
      if (oldCell.hasAttribute('aria-colindex')) newCell.setAttribute('aria-colindex', String(colNumber));
      this.suffixIds(newCell, `-c${colIndex}`);
      this.resetCellState(newCell);
      const newListbox = newCell.querySelector('[data-compare-select]');
      if (newListbox) {
        newListbox.dataset.gridSection = this.sectionId;
        newListbox.dataset.colIndex = String(colIndex);
      }
      oldCell.replaceWith(newCell);
    });

    this.currentKeys[colIndex] = newKey;

    this.refreshGridState();
    if (this.urlParamName) hidePromise.then(() => this.updateUrl());

    if (!silent) {
      const newSelector = this.getColumnSelectorCell(colIndex);
      newSelector?.querySelector('button[aria-controls]')?.focus();
    }
  }

  refreshAllOptions() {
    const otherKeysByColumn = [];
    const visibleKeys = this.getVisibleKeys();
    for (let i = 0; i < this.visibleColumns; i++) {
      otherKeysByColumn[i] = new Set(visibleKeys.filter((_, idx) => idx !== i));
    }
    for (let i = 0; i < this.visibleColumns; i++) {
      const cell = this.getColumnSelectorCell(i);
      if (!cell) continue;
      const listbox = cell.querySelector('[data-compare-select]');
      if (!listbox) continue;
      const blocked = otherKeysByColumn[i];
      listbox.querySelectorAll('button[role="option"]').forEach(option => {
        if (option.classList.contains('popover-listbox__option--current')) return;
        option.hidden = blocked.has(option.value);
      });
    }
  }

  updateUrl() {
    if (!this.urlParamName) return;
    const keys = [];
    for (let i = 0; i < this.maxColumns; i++) keys.push(this.getColumnKey(i) || '');
    while (keys.length && (!keys[keys.length - 1] || keys[keys.length - 1] === this.placeholderKey)) {
      keys.pop();
    }
    const url = new URL(location.href);
    if (keys.length) url.searchParams.set(this.urlParamName, keys.join(','));
    else url.searchParams.delete(this.urlParamName);
    const hasPlaceholderColumns = this.placeholderKey && this.currentKeys?.some(key => key === this.placeholderKey);
    if (hasPlaceholderColumns) url.searchParams.set('placeholder', 'true');
    else url.searchParams.delete('placeholder');
    history.replaceState(history.state, '', url);
  }

  setupStickyContext(signalOptions) {
    if (this.isDrawerLayout) {
      const drawer = this.closest('x-drawer');
      if (!drawer) return;
      drawer.addEventListener('dialog:after-show', () => this.scheduleStickySetup(), signalOptions);
      drawer.addEventListener('dialog:after-hide', () => {
        this.stickyIntersectionObserver?.disconnect();
        this.stickyRowgroup.classList.remove('is-stuck');
      }, signalOptions);
      return;
    }

    this.scheduleStickySetup();
    const heightObservers = document.querySelectorAll('height-observer[variable="header"], height-observer[variable="announcement-bar"]');
    if (!heightObservers.length) return;
    this.headerResizeObserver = new ResizeObserver(() => this.scheduleStickySetup());
    heightObservers.forEach(observer => this.headerResizeObserver.observe(observer));
  }

  getVisibleColumnCount() {
    if (this.smallQuery.matches || (this.midQuery.matches && this.isDrawerLayout)) return Math.min(this.maxColumns, 2);
    if (this.midQuery.matches || this.isDrawerLayout) return Math.min(this.maxColumns, 3);
    return this.maxColumns;
  }

  syncViewportState() {
    this.visibleColumns = this.getVisibleColumnCount();
    this.classList.toggle('compare-grid--all-fit', this.totalEntries <= this.visibleColumns);
  }

  revealRows() {
    if (this.skipInitialRevealRows) {
      this.showRowsWithoutReveal();
      return;
    }
    /* Skip rows hidden by the collapsible toggle — they animate later, when the
       toggle expands. We still seed their inline styles to opacity 0 so the
       expand animation has a clean starting point. */
    const targets = [];
    this.querySelectorAll('[reveal-on-scroll="true"]').forEach(row => {
      const collapsedByToggle = row.dataset.collapsedByToggle === 'true' || row.closest('[data-collapsed-by-toggle="true"]');
      if (collapsedByToggle && !this.classList.contains('is-expanded')) {
        row.style.opacity = 0;
        row.style.transform = 'translateY(0.5rem)';
      } else if (row.hidden) {
        row.style.opacity = 1;
        row.style.transform = 'none';
      } else {
        targets.push(row);
      }
    });
    this.hasRevealedRows = true;
    this.dataset.rowsRevealed = 'true';
    this.playRevealAnimation(targets);
  }

  showRowsWithoutReveal() {
    this.hasRevealedRows = true;
    this.dataset.rowsRevealed = 'true';
    this.querySelectorAll('[reveal-on-scroll="true"]').forEach(row => {
      if (row.hidden) return;
      row.style.opacity = 1;
      row.style.transform = 'none';
    });
  }

  getVisibleContentCells(row) {
    return [...row.querySelectorAll('.compare-grid__cell[data-col-index]')].filter(cell => {
      const columnIndex = parseInt(cell.dataset.colIndex, 10);
      return !cell.hidden && !Number.isNaN(columnIndex) && columnIndex < this.visibleColumns;
    });
  }

  cellHasVisibleValue(cell) {
    return cell.dataset.hasValue === 'true';
  }

  syncFirstVisibleRows() {
    this.querySelectorAll('.compare-grid__row-heading--borderless')
      .forEach(label => label.classList.remove('compare-grid__row-heading--borderless'));

    this.querySelectorAll('.compare-grid__row-group').forEach(rowgroup => {
      const visibleRows = [...rowgroup.children].filter(row => !row.hidden);

      visibleRows[1]?.querySelector('.compare-grid__row-heading')
        ?.classList.add('compare-grid__row-heading--borderless');

      visibleRows.find(row =>
        row.classList?.contains('compare-grid__row--heading')
        && !row.classList.contains('compare-grid__row--group-heading')
      )?.querySelector('.compare-grid__row-heading')
        ?.classList.add('compare-grid__row-heading--borderless');
    });
  }

  syncContentRows() {
    const groupVisibility = new Map();

    this.querySelectorAll('.compare-grid__row[data-dimension-row="true"]').forEach(row => {
      const hasVisibleValue = this.getVisibleContentCells(row).some(cell => this.cellHasVisibleValue(cell));
      row.hidden = !hasVisibleValue;
      const groupId = row.dataset.dimensionGroup;
      if (groupId && hasVisibleValue) groupVisibility.set(groupId, true);
    });

    this.querySelectorAll('.compare-grid__row[data-dimension-group-heading]').forEach(row => {
      row.hidden = !groupVisibility.get(row.dataset.dimensionGroupHeading);
    });

    this.querySelectorAll('.compare-grid__row-group--features').forEach(rowgroup => {
      const dataRows = rowgroup.querySelectorAll('.compare-grid__row[data-dimension-row="true"]');
      const rowsToCheck = dataRows.length
        ? dataRows
        : rowgroup.querySelectorAll('.compare-grid__row--content');
      rowgroup.hidden = rowsToCheck.length > 0 && ![...rowsToCheck].some(row => !row.hidden);
    });

    this.syncFirstVisibleRows();
  }

  scheduleStickySetup() {
    if (!this.stickyRowgroup) return;

    cancelAnimationFrame(this.stickySetupFrame);
    this.stickySetupFrame = requestAnimationFrame(() => this.setupStickyObserver());
  }

  setupStickyObserver() {
    if (!this.stickyRowgroup) return;

    this.stickyIntersectionObserver?.disconnect();
    const stickyTop = parseFloat(getComputedStyle(this.stickyRowgroup).top) || 0;
    const scrollContainer = this.isDrawerLayout
      ? this.closest('x-drawer')?.shadowRoot?.querySelector('[part="body"]')
      : null;
    if (this.isDrawerLayout && !scrollContainer) return;

    this.stickyIntersectionObserver = new IntersectionObserver(([entry]) => {
      const rootTop = entry.rootBounds?.top ?? 0;
      const isStuck = entry.intersectionRatio < 1 && entry.boundingClientRect.top <= rootTop + stickyTop + 1;
      this.stickyRowgroup.classList.toggle('is-stuck', isStuck);
    }, {
      root: scrollContainer || null,
      rootMargin: `-${stickyTop + 1}px 0px 0px 0px`,
      threshold: [1],
    });
    this.stickyIntersectionObserver.observe(this.stickyRowgroup);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.stickyIntersectionObserver?.disconnect();
    this.headerResizeObserver?.disconnect();
    cancelAnimationFrame(this.stickySetupFrame);
    cancelAnimationFrame(this.collapseScrollFrame);
  }
}

if (!window.customElements.get('comparison-grid')) {
  window.customElements.define('comparison-grid', ComparisonGrid);
}

class OriginalQuoteToggle extends HTMLElement {
  connectedCallback() {
    this.translatedQuote = this.querySelector('[data-translated-quote]');
    this.originalQuote = this.querySelector('[data-original-quote]');
    this.button = this.querySelector('.media-and-testimonial__original-quote-toggle');
    this.label = this.querySelector('[data-original-quote-toggle-label]');

    if (!this.translatedQuote || !this.originalQuote || !this.button || !this.label) {
      return;
    }

    this.abortController = new AbortController();
    this.showingOriginal = false;
    this.button.addEventListener('click', () => this.toggle(), { signal: this.abortController.signal });
    this.updateState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  toggle() {
    this.showingOriginal = !this.showingOriginal;
    this.updateState();
  }

  updateState() {
    this.translatedQuote.hidden = this.showingOriginal;
    this.originalQuote.hidden = !this.showingOriginal;
    this.button.setAttribute('aria-pressed', String(this.showingOriginal));
    this.label.textContent = this.showingOriginal ? this.dataset.showTranslation : this.dataset.showOriginal;
  }
}

if (!window.customElements.get('original-quote-toggle')) {
  window.customElements.define('original-quote-toggle', OriginalQuoteToggle);
}

(function syncEvReferralToCart() {
  const evReferral = sessionStorage.getItem('EV.ev_referral');
  if (!evReferral) return;

  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { 'ev-customer': 'true' } }),
  }).catch(() => {});
})();

export {
  OrtliebAnnouncementBar,
  GalleryMoreButton,
  Modal,
  OnSiteSearchBar,
  FilterRange,
  ThreeSixtyViewer,
  BuyLocalButton,
  ComparisonGrid,
};
